// useWebSocket.ts - Hook personalizado para WebSocket
import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../shared/hooks/useAuth';
import { useTenant } from '../contexts/TenantContext';

interface UseWebSocketOptions {
    autoConnect?: boolean;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
    const { autoConnect = true } = options;
    const { user } = useAuth();
    const { tenant } = useTenant();
    const socketRef = useRef<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!autoConnect || !user || !tenant) return;

        const connectSocket = async () => {
            try {
                const token = await user.getIdToken();
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

                const socket = io(apiUrl, {
                    auth: {
                        token
                    },
                    extraHeaders: {
                        'x-tenant-id': tenant.id
                    },
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionAttempts: 5
                });

                socket.on('connect', () => {
                    console.log('✅ WebSocket conectado');
                    setIsConnected(true);
                    setError(null);
                });

                socket.on('disconnect', () => {
                    console.log('❌ WebSocket desconectado');
                    setIsConnected(false);
                });

                socket.on('connect_error', (err) => {
                    console.error('❌ Error de conexión WebSocket:', err);
                    setError(err.message);
                    setIsConnected(false);
                });

                socketRef.current = socket;
            } catch (err: any) {
                console.error('Error al conectar WebSocket:', err);
                setError(err.message);
            }
        };

        connectSocket();

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [user, tenant, autoConnect]);

    const subscribeToPackage = (packageId: string) => {
        if (socketRef.current && isConnected) {
            socketRef.current.emit('subscribe:package', packageId);
        }
    };

    const unsubscribeFromPackage = (packageId: string) => {
        if (socketRef.current && isConnected) {
            socketRef.current.emit('unsubscribe:package', packageId);
        }
    };

    const subscribeToCourier = (courierId: string) => {
        if (socketRef.current && isConnected) {
            socketRef.current.emit('subscribe:courier', courierId);
        }
    };

    const on = (event: string, callback: (...args: any[]) => void) => {
        if (socketRef.current) {
            socketRef.current.on(event, callback);
        }
    };

    const off = (event: string, callback?: (...args: any[]) => void) => {
        if (socketRef.current) {
            if (callback) {
                socketRef.current.off(event, callback);
            } else {
                socketRef.current.off(event);
            }
        }
    };

    return {
        socket: socketRef.current,
        isConnected,
        error,
        subscribeToPackage,
        unsubscribeFromPackage,
        subscribeToCourier,
        on,
        off
    };
};

export default useWebSocket;
